import React from 'react';
import { useConfig } from '../hooks/useConfig';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Avatar,
  Chip,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import BuildIcon from '@mui/icons-material/Build';
import GitHubIcon from '@mui/icons-material/GitHub';
import PaymentIcon from '@mui/icons-material/Payment';

const techStack = [
  // Frontend
  {
    name: 'React 18',
    category: 'Frontend',
    description: 'Frontend library for building responsive UI components',
    icon: <CodeIcon />,
    color: '#61DAFB'
  },
  {
    name: 'Material-UI (MUI v5)',
    category: 'Frontend',
    description: 'React UI framework for consistent design system and components',
    icon: <BuildIcon />,
    color: '#007FFF'
  },
  {
    name: 'Node.js & npm',
    category: 'Frontend',
    description: 'JavaScript runtime and package manager for frontend development',
    icon: <CodeIcon />,
    color: '#339933'
  },
  
  // Backend
  {
    name: 'Python 3',
    category: 'Backend',
    description: 'Primary backend programming language',
    icon: <StorageIcon />,
    color: '#3776AB'
  },
  {
    name: 'Flask',
    category: 'Backend',
    description: 'Python web framework for building the API and server-side logic',
    icon: <StorageIcon />,
    color: '#000000'
  },
  {
    name: 'Gunicorn',
    category: 'Backend',
    description: 'WSGI HTTP Server for Python applications',
    icon: <StorageIcon />,
    color: '#499854'
  },
  
  // AI/ML
  {
    name: 'OpenAI API',
    category: 'AI/ML',
    description: 'Advanced language models for natural language processing',
    icon: <BuildIcon />,
    color: '#412991'
  },
  {
    name: 'Pinecone',
    category: 'AI/ML',
    description: 'Vector database for similarity search and retrieval-augmented generation',
    icon: <StorageIcon />,
    color: '#FF6B6B'
  },
  
  // Infrastructure
  {
    name: 'Google Cloud Run',
    category: 'Infrastructure',
    description: 'Serverless container platform for deploying the application',
    icon: <CloudIcon />,
    color: '#4285F4'
  },
  {
    name: 'Docker',
    category: 'Infrastructure',
    description: 'Containerization platform for consistent deployment',
    icon: <CloudIcon />,
    color: '#2496ED'
  },
  
  // Authentication & Security
  {
    name: 'Google Identity / OAuth2',
    category: 'Security',
    description: 'Secure user authentication and authorization',
    icon: <SecurityIcon />,
    color: '#34A853'
  },
  
  // Database
  {
    name: 'Google Firestore',
    category: 'Database',
    description: 'NoSQL document database for storing application data',
    icon: <StorageIcon />,
    color: '#FFCA28'
  },
  
  // DevOps
  {
    name: 'GitHub Actions',
    category: 'DevOps',
    description: 'Automated testing and deployment pipeline',
    icon: <GitHubIcon />,
    color: '#181717'
  },
  
  // Testing
  {
    name: 'Pytest',
    category: 'Testing',
    description: 'Python testing framework',
    icon: <BuildIcon />,
    color: '#0A9EDC'
  },
  {
    name: 'Jest',
    category: 'Testing',
    description: 'JavaScript testing framework',
    icon: <BuildIcon />,
    color: '#C21325'
  },
  
  // Payments
  {
    name: 'PayPal',
    category: 'Payments',
    description: 'Secure payment processing integration',
    icon: <PaymentIcon />,
    color: '#00457C'
  }
];

const CategoryChip = ({ category }) => {
  const theme = useTheme();
  const categoryColors = {
    'Frontend': theme.palette.primary.main,
    'Backend': theme.palette.secondary.main,
    'Infrastructure': theme.palette.info.main,
    'Authentication': theme.palette.success.main,
    'Database': theme.palette.warning.main,
    'CI/CD': theme.palette.error.main,
    'Payments': theme.palette.info.dark
  };

  return (
    <Chip
      label={category}
      size="small"
      sx={{
        backgroundColor: categoryColors[category] || theme.palette.grey[300],
        color: theme.palette.getContrastText(categoryColors[category] || theme.palette.grey[300])
      }}
    />
  );
};

export default function AboutDialog({ open, onClose }) {
  const { app, loading: configLoading, getAppTitle, getAppDescription, getCompanyDescription } = useConfig();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography variant="h5">About {getAppTitle('GeoDataBot')}</Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Version {configLoading ? 'Loading...' : app.version}
        </Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {/* First Paragraph: App Long Description */}
        <Typography paragraph>
          {configLoading 
            ? 'Loading application information...' 
            : getAppDescription('long', `${getAppTitle('GeoDataBot')} is an experimental Retrieval-Augmented Generation chatbot platform developed by Intelligent Data Design Inc. Our goal is to deliver secure, scalable, and privacy-centric AI assistants that combine retrieval systems with state-of-the-art language models.`)
          }
        </Typography>
        
        {/* Second Paragraph: Company Description with HTML link */}
        <Typography 
          paragraph
          dangerouslySetInnerHTML={{
            __html: configLoading 
              ? 'Loading company information...'
              : getCompanyDescription('Intelligent Data Design, Inc., is an AI/Database technology company, providing superior engineering solutions since 2013.')
          }}
        />
        <Typography variant="h6" gutterBottom>
          Technology Stack
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <Table size="small" aria-label="tech stack">
            <TableBody>
              {techStack.map((tech) => (
                <TableRow
                  key={tech.name}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell sx={{ width: 60, px: 1 }}>
                    <Avatar sx={{ bgcolor: tech.color, width: 32, height: 32 }}>
                      {React.cloneElement(tech.icon, { sx: { fontSize: 18 } })}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" component="div">
                          {tech.name}
                        </Typography>
                        <CategoryChip category={tech.category} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {tech.description}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Intelligent Data Design Inc.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
